import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './TestList.module.css';
import AddTestForm from '../component/AddTestForm';
import { ROLES } from '../config/roles';

const TestList = () => {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [testQuery, setTestQuery] = useState('');
    const userRole = localStorage.getItem('role');
    const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;

    const fetchTestList = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/testlist`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setTests(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch test list.');
            console.error('Error fetching test list:', err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTestList();
    }, [fetchTestList]);

    const handleExportTests = () => {
        const filtered = tests.filter(test => {
            const tq = testQuery.trim().toLowerCase();
            return !tq || (test.TestName || '').toLowerCase().includes(tq);
        });
        if (!filtered.length) {
            alert('No data available to export.');
            return;
        }
        const data = filtered.map((test, i) => ({
            'Sl. No.': i + 1,
            'Test Name': test.TestName || '',
            'TAT (Days)': test.tat ?? '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 8 }, { wch: 36 }, { wch: 12 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Test List');
        XLSX.writeFile(wb, 'Test_List.xlsx');
    };

    const openAddModal = () => {
        setEditData(null);
        setShowModal(true);
    };

    const openEditModal = (test) => {
        setEditData(test);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditData(null);
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Test List</h2>
            </div>

            {/* ─── Toolbar: search (left) · actions (right) ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 auto' }}>
                    <input
                        type="text"
                        placeholder="Search by test name"
                        value={testQuery}
                        onChange={e => setTestQuery(e.target.value)}
                        style={{ width: '100%', maxWidth: 320, padding: '9px 12px', fontSize: 14, border: '1.5px solid #d0d7de', borderRadius: 8 }}
                    />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                        className={styles.exportIconBtn}
                        title="Export to Excel"
                        aria-label="Export to Excel"
                        onClick={handleExportTests}
                    >
                        <FaFileExcel size={20} />
                    </button>
                    {isSuperAdmin && (
                        <button className={styles.addBtn} onClick={openAddModal}>
                            + Add Test
                        </button>
                    )}
                </div>
            </div>
            <AddTestForm
                show={showModal}
                onClose={closeModal}
                onSuccess={fetchTestList}
                editData={editData}
            />
            <ToastContainer position="top-right" autoClose={3000} />
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Test Name</th>
                        <th>TAT (Days)</th>
                        {isSuperAdmin && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {tests
                        .filter(test => {
                            const tq = testQuery.trim().toLowerCase();
                            return !tq || (test.TestName || '').toLowerCase().includes(tq);
                        })
                        .map(test => (
                        <tr key={test.id}>
                            <td>{test.id}</td>
                            <td>{test.TestName}</td>
                            <td>{test.tat}</td>
                            {isSuperAdmin && (
                                <td>
                                    <button
                                        className={styles.editBtn}
                                        onClick={() => openEditModal(test)}
                                        title="Edit test"
                                    >
                                        ✏️
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TestList;
